import {
  Box,
  Typography,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { NEW_PRODUCT, LINE_KINDS } from "./helpers";
import {
  fieldLabelStyle,
  selectStyle,
  getSelectMenuProps,
  textFieldStyle,
  readOnlyFieldStyle,
  headerRowStyle,
  headerColStyle,
  fieldErrorStyle,
} from "./styles";

// One "Product" line card in the Edit Order editor (COMY1-292). "Choose Product"
// lists the supplier's existing products plus a "New Product" option. Existing
// products prefill name + price read-only; "New Product" is a fully editable
// ad-hoc line.
const ProductLineCard = ({
  index,
  line,
  productOptions,
  categoryOptions,
  currencySymbol,
  changeKind,
  setLineField,
  errors,
  showErrors,
}) => {
  const { t, i18n } = useTranslation("view-job-modal");
  const menuProps = getSelectMenuProps(i18n.language === "he");

  const selectValue = line.is_new
    ? NEW_PRODUCT
    : line.product_id === ""
    ? ""
    : String(line.product_id);

  const handleProductChange = (value) => {
    if (value === NEW_PRODUCT) {
      setLineField(index, "is_new", true);
      setLineField(index, "product_id", "");
      setLineField(index, "product_name", "");
      setLineField(index, "product_price", "");
      setLineField(index, "category_id", "");
      setLineField(index, "unit_price", "");
      return;
    }
    const prod = productOptions.find((p) => String(p.id) === String(value));
    setLineField(index, "is_new", false);
    setLineField(index, "product_id", value);
    setLineField(index, "product_name", prod?.name || "");
    setLineField(index, "product_price", prod?.price ?? "");
    setLineField(index, "category_id", prod?.category_id ?? "");
    setLineField(index, "unit_price", prod?.price ?? "");
  };

  return (
    <Box display="flex" flexDirection="column" gap="16px">
      {/* Header: Choose Service or Product | Choose Product */}
      <Box sx={headerRowStyle}>
        <Box sx={headerColStyle}>
          <Typography sx={fieldLabelStyle}>{t("edit.choose_type")}</Typography>
          <Select
            fullWidth
            size="small"
            value={line.kind}
            onChange={(e) => changeKind(index, e.target.value)}
            sx={selectStyle}
          >
            <MenuItem value={LINE_KINDS.SERVICE}>
              {t("edit.type_service")}
            </MenuItem>
            <MenuItem value={LINE_KINDS.PRODUCT}>
              {t("edit.type_product")}
            </MenuItem>
          </Select>
        </Box>

        <Box sx={headerColStyle}>
          <Typography sx={fieldLabelStyle}>
            {t("edit.choose_product")}
          </Typography>
          <Select
            fullWidth
            size="small"
            displayEmpty
            value={selectValue}
            onChange={(e) => handleProductChange(e.target.value)}
            sx={selectStyle}
            MenuProps={menuProps}
            renderValue={(selected) => {
              if (selected === "" || selected == null) {
                return (
                  <Typography
                    sx={{ color: "rgba(62, 63, 76, 0.4)", fontSize: "14px" }}
                  >
                    {t("edit.choose_product")}
                  </Typography>
                );
              }
              if (selected === NEW_PRODUCT) return t("edit.new_product");
              const prod = productOptions.find(
                (p) => String(p.id) === String(selected)
              );
              return prod?.name || line.product_name || selected;
            }}
          >
            <MenuItem
              value={NEW_PRODUCT}
              sx={{ fontWeight: 600, color: "rgba(36, 139, 112, 1)" }}
            >
              {t("edit.new_product")}
            </MenuItem>
            {productOptions.map((p) => (
              <MenuItem key={p.id} value={String(p.id)}>
                {p.name}
              </MenuItem>
            ))}
          </Select>
          {showErrors && errors?.product_id && (
            <Typography sx={fieldErrorStyle}>{errors.product_id}</Typography>
          )}
        </Box>
      </Box>

      {(line.is_new || line.product_id !== "") && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: "12px",
            alignItems: "flex-start",
          }}
        >
          {/* Product name */}
          <Box sx={{ flex: "1 1 200px", minWidth: "180px" }}>
            <Typography sx={fieldLabelStyle}>{t("edit.product_name")}</Typography>
            <TextField
              size="small"
              fullWidth
              value={line.product_name}
              onChange={(e) =>
                setLineField(index, "product_name", e.target.value)
              }
              InputProps={{ readOnly: !line.is_new }}
              sx={line.is_new ? textFieldStyle : readOnlyFieldStyle}
            />
            {showErrors && errors?.product_name && (
              <Typography sx={fieldErrorStyle}>{errors.product_name}</Typography>
            )}
          </Box>

          {/* Product amount (quantity) */}
          <Box sx={{ flex: "0 0 100px" }}>
            <Typography sx={fieldLabelStyle}>{t("edit.product_amount")}</Typography>
            <TextField
              size="small"
              fullWidth
              value={line.quantity}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "" || /^\d*$/.test(v)) {
                  setLineField(index, "quantity", v);
                }
              }}
              inputProps={{ inputMode: "numeric" }}
              sx={textFieldStyle}
            />
            {showErrors && errors?.quantity && (
              <Typography sx={fieldErrorStyle}>{errors.quantity}</Typography>
            )}
          </Box>

          {/* Product price — currency sign OUTSIDE the box (dir=ltr wrapper
              keeps ₪ on the left), input text stays RTL. Matches the variant
              not-listed price for consistency. */}
          <Box sx={{ flex: "0 0 130px" }}>
            <Typography sx={fieldLabelStyle}>{t("edit.product_price")}</Typography>
            <Box
              dir="ltr"
              sx={{ display: "flex", alignItems: "center", gap: "6px" }}
            >
              <Typography
                sx={{ color: "rgba(47, 43, 61, 0.6)", fontSize: "14px" }}
              >
                {currencySymbol}
              </Typography>
              <TextField
                size="small"
                fullWidth
                value={line.product_price}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "" || /^\d*\.?\d{0,2}$/.test(v)) {
                    setLineField(index, "product_price", v);
                    setLineField(index, "unit_price", v);
                  }
                }}
                InputProps={{ readOnly: !line.is_new }}
                inputProps={{ style: { direction: "rtl", textAlign: "right" } }}
                sx={line.is_new ? textFieldStyle : readOnlyFieldStyle}
              />
            </Box>
            {showErrors && errors?.product_price && (
              <Typography sx={fieldErrorStyle}>{errors.product_price}</Typography>
            )}
          </Box>

          {/* Category (New Product only) */}
          {line.is_new && (
            <Box sx={{ flex: "1 1 200px", minWidth: "180px" }}>
              <Typography sx={fieldLabelStyle}>{t("edit.category")}</Typography>
              <Select
                fullWidth
                size="small"
                displayEmpty
                value={line.category_id === "" ? "" : String(line.category_id)}
                onChange={(e) =>
                  setLineField(index, "category_id", e.target.value)
                }
                sx={selectStyle}
                MenuProps={menuProps}
                renderValue={(selected) => {
                  if (selected === "" || selected == null) {
                    return (
                      <Typography
                        sx={{ color: "rgba(62, 63, 76, 0.4)", fontSize: "14px" }}
                      >
                        {t("edit.category")}
                      </Typography>
                    );
                  }
                  const opt = categoryOptions.find(
                    (o) => String(o.id) === String(selected)
                  );
                  return opt?.label || selected;
                }}
              >
                {categoryOptions.map((o) => (
                  <MenuItem
                    key={o.id}
                    value={String(o.id)}
                    disabled={o.isParent}
                    sx={{
                      pl: o.isParent ? "16px" : "32px",
                      fontWeight: o.isParent ? 600 : 400,
                      fontSize: "14px",
                    }}
                  >
                    {o.name}
                  </MenuItem>
                ))}
              </Select>
              {showErrors && errors?.category_id && (
                <Typography sx={fieldErrorStyle}>{errors.category_id}</Typography>
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ProductLineCard;
